import Box from '@mui/material/Box';
import React from 'react';

import { AddNotePanel } from './panels/AddNotePanel';
import { EquipmentFaultPanel } from './panels/EquipmentFaultPanel';
import { HistoryNotesPanel } from './panels/HistoryNotesPanel';
import { JobDetailsPanel } from './panels/JobDetailsPanel';
import { LocationPanel } from './panels/LocationPanel';
import {
  findNodeById,
  findNodeByType,
  findParentOutletForNodeId,
} from './panels/LocationPanel/HierarchyTree';
import { PartsShipmentsPanel } from './panels/PartsShipmentsPanel';
import { RelatedJobsPanel } from './panels/RelatedJobsPanel';
import type { useServiceDetailData } from './useServiceDetailData';
import { ActionPermissions } from '../../../@types';
import type { CustomPermission } from '../../../@types';
import type { ServiceObject } from '../../../@types/service.type';
import { AttachmentsPanel } from '../../../components/organisms/AttachmentsPanel';
import { LinkJobButton } from '../../../components/organisms/LinkJobButton';
import { isUserHasPermission } from '../../../configs/permissions';

export interface ServiceDetailsTabProps {
  service: ServiceObject;
  serviceId: number;
  isClient: boolean;
  permissions?: CustomPermission[];
  data: ReturnType<typeof useServiceDetailData>;
}

// The node IDs the BFF needs to build the dispatch-destination suggestions
// (assigned technician + parent depot, job main/sub location) — same sourcing
// as the legacy DispatchShipmentFragment.
const getDestinationSuggestionNodeIds = (service: ServiceObject) => ({
  mainLocationNodeId: service.mainLocationNodeId,
  subLocationNodeId: service.subLocationNodeId,
  primaryAllocationNodeId: service.primaryAllocation?.node?.id,
  secondaryAllocationNodeId: service.secondaryAllocation?.node?.id,
});

/**
 * The Details tab body for ServiceDetail. Hidden panels for client/customer
 * users (PartsShipmentsPanel, RelatedJobsPanel) are gated here in one place
 * so the parent ServiceDetail stays small and the rules are easy to audit.
 *
 * AddNotePanel sits in the left column for internal users (the right column
 * already carries PartsShipments + RelatedJobs) and moves to the right column
 * for clients to keep that column balanced now that the internal panels are
 * suppressed.
 */
export const ServiceDetailsTab: React.FC<ServiceDetailsTabProps> = ({
  service,
  serviceId,
  isClient,
  permissions,
  data,
}) => {
  // Action Type is applied by the BFF only under the process/action-types edit
  // permission, so gate the inline editor on it (not the general-edit gate the
  // date fields use).
  const canEditActionType = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Parameters_Process_Action,
    permissions
  );

  const shipmentLocationHierarchy = service.locationHierarchy ?? null;
  const shipmentMainLocationNode =
    service.mainLocationNodeId != null
      ? findNodeById(shipmentLocationHierarchy, service.mainLocationNodeId)
      : null;
  const shipmentDestinationOutlet =
    findParentOutletForNodeId(shipmentLocationHierarchy, service.mainLocationNodeId) ??
    (shipmentMainLocationNode?.nodeTypeCode === 'outlet' ? shipmentMainLocationNode : null) ??
    findNodeByType(shipmentLocationHierarchy, 'outlet');

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 3fr',
          gap: 1.75,
          px: 2.5,
          pt: 1.75,
          alignItems: 'start',
          '@media print': { gridTemplateColumns: '1fr' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.75,
            '@media print': { display: 'none' },
          }}
        >
          <JobDetailsPanel
            service={service}
            isClient={isClient}
            onFieldUpdate={data.canEditReferences ? data.handleFieldUpdate : undefined}
            actionTypeOptions={data.actionTypeOptions}
            onActionTypeUpdate={
              canEditActionType
                ? (code) => data.handleFieldUpdate('actionTypeCode', code)
                : undefined
            }
          />
          <EquipmentFaultPanel
            service={service}
            canEditReferences={data.canEditReferences}
            canEditReportedInfo={data.canEditReportedInfo}
            canEditReportedSerial={data.canEditReportedSerial}
            reportedModelOptions={data.reportedModelOptions}
            reportedFaultOptions={data.reportedFaultOptions}
            onReportedInfoUpdate={data.handleReportedInfoUpdate}
            onSerialPick={data.handleSerialPick}
          />
          {!isClient && <AddNotePanel onAddNote={data.handleAddNote} />}
          <AttachmentsPanel entityId={serviceId} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <LocationPanel
            service={service}
            nodeAttributes={data.nodeAttrsQuery.data as unknown as Array<Record<string, unknown>>}
            tradingHoursData={
              data.tradingHoursQuery.data as unknown as Array<Record<string, unknown>>
            }
            coverageHoursData={
              data.coverageHoursQuery.data as unknown as Array<Record<string, unknown>>
            }
            permissions={permissions}
            onSaveAddress={data.handleSaveAddress}
            onSaveContact={data.handleSaveContact}
          />
          {/* AddNote, Parts, RelatedJobs — hidden during printing; only Location is printed */}
          <Box sx={{ display: 'contents', '@media print': { display: 'none' } }}>
            {isClient && <AddNotePanel onAddNote={data.handleAddNote} />}
            {!isClient && (
              <PartsShipmentsPanel
                serviceId={serviceId}
                destinationNodeId={shipmentDestinationOutlet?.id ?? service.mainLocationNodeId}
                destinationName={shipmentDestinationOutlet?.name ?? service.mainLocation}
                {...getDestinationSuggestionNodeIds(service)}
                clientHierarchy={service.clientHierarchy}
                clientContractIds={data.clientContractIds}
                clientName={service.client ?? ''}
                contractName={service.contract ?? ''}
                permissions={permissions}
              />
            )}
            {!isClient && (
              <RelatedJobsPanel
                currentServiceId={serviceId}
                otherJobsAtLocation={data.otherJobsAtLocation}
                linkedJobs={data.linkedJobs}
                actions={<LinkJobButton serviceId={serviceId} permissions={permissions} />}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: 2.5, py: 1.75, '@media print': { display: 'none' } }}>
        <HistoryNotesPanel
          logs={data.logs}
          serviceId={serviceId}
          permissions={permissions}
          attendanceMetrics={data.attendanceMetrics}
        />
      </Box>
    </>
  );
};
